import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_ImageSprites = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🎨 CSS Image Sprites</h1>

      <p className="tutorial-intro">
        An <b>image sprite</b> is a single image file that contains multiple
        icons/images. With CSS, you show only part of it using{" "}
        <code>background-position</code>. This reduces HTTP requests.
      </p>

      <div
        style={{
          background: "linear-gradient(135deg, #fde68a, #fcd34d)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          ✨ Why Use CSS Sprites?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Sprites combine multiple images into one. It reduces the number of
          image requests and helps performance. Also useful for icons,
          navigation, UI elements.
        </p>
      </div>

      {/* Example 1: Basic Sprite */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Basic Sprite</h2>
        <p>
          Here is a sprite sheet containing several icons (home, search, user).
          We'll show each icon via background-position.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
.icon {
  width: 32px;
  height: 32px;
  background: url("https://raw.githubusercontent.com/encharm/Font-Awesome-SVG-PNG/master/black/png/32/github-alt.png") no-repeat;
  /* just example: using a simple sprite-like image */
}

.icon-home {
  background-position: 0 0;
}

.icon-search {
  background-position: -32px 0;
}

.icon-user {
  background-position: -64px 0;
}`}
          language="css"
        />

        <div style={{ display: "flex", gap: "15px", marginTop: "1rem" }}>
          <div
            className="icon-home"
            style={{
              width: "32px",
              height: "32px",
              background: `url("https://raw.githubusercontent.com/encharm/Font-Awesome-SVG-PNG/master/black/png/32/github-alt.png") no-repeat`,
              backgroundPosition: "0 0",
              border: "1px solid #ddd",
            }}
          ></div>
          <div
            className="icon-search"
            style={{
              width: "32px",
              height: "32px",
              background: `url("https://raw.githubusercontent.com/encharm/Font-Awesome-SVG-PNG/master/black/png/32/github-alt.png") no-repeat`,
              backgroundPosition: "-15px 0",
              border: "1px solid #ddd",
            }}
          ></div>
          <div
            className="icon-user"
            style={{
              width: "32px",
              height: "32px",
              background: `url("https://raw.githubusercontent.com/encharm/Font-Awesome-SVG-PNG/master/black/png/32/github-alt.png") no-repeat`,
              backgroundPosition: "-64px 0",
              border: "1px solid #ddd",
            }}
          ></div>
        </div>
      </div>

      {/* Example 2: Hover Sprite */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Hover Effect</h2>
        <p>
          On hover, shift the background position to show a “hover” version in
          the sprite.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
.btn-icon {
  width: 34px;
  height: 34px;
  background: url("https://raw.githubusercontent.com/encharm/Font-Awesome-SVG-PNG/master/black/png/32/github-alt.png") no-repeat;
}

.btn-icon:hover {
  background-color = "red"
}`}
          language="css"
        />

        <div
          className="btn-icon"
          style={{
            width: "34px",
            height: "34px",
            background: `url("https://raw.githubusercontent.com/encharm/Font-Awesome-SVG-PNG/master/black/png/32/github-alt.png") no-repeat`,
            border: "1px solid #ddd",
            marginTop: "1rem",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "red")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#ffffffff")
          }
        ></div>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Use your own sprite sheets (you can upload them) and adjust the CSS
          positions based on the grid. Try creating sprite-based navigation or
          UI icon bars.
        </p>
      </div>
    </div>
  );
};

export default CSS_ImageSprites;
